import { Box } from "@bcc-ui/ui-components";
import React, { useEffect, useState } from "react";

import {
    buildLockedAssetsDistributionRequest,
    EMPTY_LOCKED_ASSETS_DISTRIBUTION,
    LockedAssetsDistribution,
    toLockedAssetsDistribution,
} from "./cards/locked-assets-distribution-card/lockedAssetsDistribution";
import { LockedAssetsDistributionCard } from "./cards/locked-assets-distribution-card/LockedAssetsDistributonCard";
import { RiskDistributionCard } from "./cards/risk-distribution-card/RiskDistributionCard";
import {
    buildErasureCertificateDistributionRequest,
    toErasureCertificateAssets,
} from "./cards/risk-factors-card/erasureCertificates";
import {
    ALL_LOCK_TYPES,
    buildLockedDeviceDistributionRequest,
    LockTypeFilter,
    toLockedDeviceAssets,
} from "./cards/risk-factors-card/lockedDevices";
import { ErasureCertificatesData, LockedDevicesData, RiskFactorsCard } from "./cards/risk-factors-card/RiskFactorsCard";
import style from "./risk-tab.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { RiskLevel } from "domain/asset-management/assetFilterConstants";
import { AssetSearchRequest, assetService, RiskSummary } from "services/asset-management/AssetService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

const FIRST_PAGE = 1;

const INITIAL_LOCKED_DEVICES: LockedDevicesData = {
    assets: [],
    lockTypeFilter: ALL_LOCK_TYPES,
    currentPage: FIRST_PAGE,
    totalPages: 0,
    totalAssets: 0,
    isLoading: true,
};

const INITIAL_ERASURE_CERTIFICATES: ErasureCertificatesData = {
    assets: [],
    currentPage: FIRST_PAGE,
    totalPages: 0,
    totalAssets: 0,
    isLoading: true,
};

interface Props {
    onViewDevices?: (riskLevel: RiskLevel) => void;
    assetSearchRequest?: AssetSearchRequest;
    refreshKey?: number;
}

export const RiskTab = (props: Props) => {
    const [riskSummary, setRiskSummary] = useState<RiskSummary | null>(null);
    const [lockedAssetsDistribution, setLockedAssetsDistribution] = useState<LockedAssetsDistribution | null>(null);
    const [lockedDevices, setLockedDevices] = useState<LockedDevicesData>(INITIAL_LOCKED_DEVICES);
    const [erasureCertificates, setErasureCertificates] =
        useState<ErasureCertificatesData>(INITIAL_ERASURE_CERTIFICATES);
    const [erasureRefreshCounter, setErasureRefreshCounter] = useState(0);

    useEffect(() => {
        const abortController = new AbortController();
        assetService
            .fetchRiskSummary(props.assetSearchRequest, abortController)
            .then(setRiskSummary)
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch risk summary: ", error);
                }
            });
        assetService
            .fetchLockDistributions(buildLockedAssetsDistributionRequest(props.assetSearchRequest), abortController)
            .then((distributions) => setLockedAssetsDistribution(toLockedAssetsDistribution(distributions)))
            .catch((error: Error) => {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error("Failed to fetch locked assets distribution: ", error);
                setLockedAssetsDistribution(EMPTY_LOCKED_ASSETS_DISTRIBUTION);
            });
        return () => abortController.abort();
    }, [props.refreshKey]);

    useEffect(() => {
        const abortController = new AbortController();
        setLockedDevices((previous) => ({ ...previous, isLoading: true }));
        assetService
            .fetchRiskDistributions(
                buildLockedDeviceDistributionRequest(
                    lockedDevices.lockTypeFilter,
                    lockedDevices.currentPage,
                    props.assetSearchRequest
                ),
                abortController
            )
            .then((response) => {
                setLockedDevices((previous) => ({
                    ...previous,
                    assets: toLockedDeviceAssets(response.assets),
                    totalPages: response.totalPages,
                    totalAssets: response.totalAssets ?? response.assets.length,
                    isLoading: false,
                }));
            })
            .catch((error: Error) => {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error("Failed to fetch locked devices for the risk factors card: ", error);
                setLockedDevices((previous) => ({
                    ...previous,
                    assets: [],
                    totalPages: 0,
                    totalAssets: 0,
                    isLoading: false,
                }));
            });
        return () => abortController.abort();
    }, [lockedDevices.lockTypeFilter, lockedDevices.currentPage, props.refreshKey]);

    useEffect(() => {
        const abortController = new AbortController();
        setErasureCertificates((previous) => ({ ...previous, isLoading: true }));
        assetService
            .fetchRiskDistributions(
                buildErasureCertificateDistributionRequest(erasureCertificates.currentPage, props.assetSearchRequest),
                abortController
            )
            .then((response) => {
                setErasureCertificates((previous) => ({
                    ...previous,
                    assets: toErasureCertificateAssets(response.assets),
                    totalPages: response.totalPages,
                    totalAssets: response.totalAssets ?? response.assets.length,
                    isLoading: false,
                }));
            })
            .catch((error: Error) => {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error("Failed to fetch erasure certificates for the risk factors card: ", error);
                setErasureCertificates((previous) => ({
                    ...previous,
                    assets: [],
                    totalPages: 0,
                    totalAssets: 0,
                    isLoading: false,
                }));
            });
        return () => abortController.abort();
    }, [erasureCertificates.currentPage, erasureRefreshCounter, props.refreshKey]);

    const selectLockTypeFilter = (lockTypeFilter: LockTypeFilter) => {
        setLockedDevices((previous) => ({ ...previous, lockTypeFilter, currentPage: FIRST_PAGE }));
    };

    if (riskSummary === null || lockedAssetsDistribution === null) {
        return <LoadingIndicator />;
    }

    return (
        <Box
            gap={"Sm"}
            paddingBottom={"Md"}
            className={style.cardsContainer}
            testId={testIds.workArea.asset.customView.risk.itself}
        >
            <Box columnLayout className={style.leftSection}>
                <RiskFactorsCard
                    lockedDevices={lockedDevices}
                    erasureCertificates={erasureCertificates}
                    onLockTypeFilterSelect={selectLockTypeFilter}
                    onLockedDevicesPageChange={(page) =>
                        setLockedDevices((previous) => ({ ...previous, currentPage: page }))
                    }
                    onErasureCertificatesPageChange={(page) =>
                        setErasureCertificates((previous) => ({ ...previous, currentPage: page }))
                    }
                    onErasureReportUploaded={() => setErasureRefreshCounter((previous) => previous + 1)}
                />
            </Box>
            <Box columnLayout gap={"Sm"} className={style.rightSection}>
                <RiskDistributionCard data={riskSummary} />
                <LockedAssetsDistributionCard data={lockedAssetsDistribution} />
            </Box>
        </Box>
    );
};
